import { TreeNode } from "@/types/tree";
import { getNearestNodeThatCanBeActivatedInFutureLayerMode } from "@/futurelayer/canvas/element-behavior/element-behavior";
import { findNearestTreeNodeFromHTMLElement } from "./util";
import {
  findNearestRepeatableItem,
  findNearestWrapperLink
} from "./grid-and-link-wrapper-detection";
import { findHoveredGalleryImage, HoveredGalleryImageData } from "@/lib/singularity/gallery";

export type HoveredElementData = {
  nearestNodeThatCanBeActivated: TreeNode | null;
  nearestRepeatableItem: TreeNode | null;
  nearestWrapperLink: TreeNode | null;
  hoveredGalleryImage: HoveredGalleryImageData;
};

type HoveredElementCallback = (data: HoveredElementData) => void;

export class HoveredElementService {
  private callback!: HoveredElementCallback;
  private hoveredElementData: HoveredElementData = {
    nearestNodeThatCanBeActivated: null,
    nearestRepeatableItem: null,
    nearestWrapperLink: null,
    hoveredGalleryImage: null
  };
  private cleanup!: () => void;

  constructor(callback: HoveredElementCallback) {
    this.callback = callback;
    this.startListening();
  }

  destroy(): void {
    this.stopListening();
  }

  private updateHoveredElementData(newData: HoveredElementData): void {
    if (
      newData.nearestNodeThatCanBeActivated !==
        this.hoveredElementData.nearestNodeThatCanBeActivated ||
      newData.nearestRepeatableItem !== this.hoveredElementData.nearestRepeatableItem ||
      newData.nearestWrapperLink !== this.hoveredElementData.nearestWrapperLink ||
      newData.hoveredGalleryImage !== this.hoveredElementData.hoveredGalleryImage
    ) {
      this.hoveredElementData = newData;
      this.callback(newData);
    }
  }

  private startListening() {
    const canvasDocument = window.Breakdance.canvas.document;

    const handleMouseMove = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const treeNode = findNearestTreeNodeFromHTMLElement(target);

      const hoveredGalleryImage = findHoveredGalleryImage(target, treeNode);

      this.updateHoveredElementData({
        nearestNodeThatCanBeActivated: treeNode
          ? getNearestNodeThatCanBeActivatedInFutureLayerMode(treeNode)
          : null,
        nearestRepeatableItem: treeNode ? findNearestRepeatableItem(treeNode) : null,
        nearestWrapperLink: treeNode ? findNearestWrapperLink(treeNode) : null,
        hoveredGalleryImage
      });
    };

    const handleMouseLeave = () => {
      this.updateHoveredElementData({
        nearestNodeThatCanBeActivated: null,
        nearestRepeatableItem: null,
        nearestWrapperLink: null,
        hoveredGalleryImage: null
      });
    };

    canvasDocument.addEventListener("mousemove", handleMouseMove);
    canvasDocument.addEventListener("mouseleave", handleMouseLeave);

    this.cleanup = () => {
      canvasDocument.removeEventListener("mousemove", handleMouseMove);
      canvasDocument.removeEventListener("mouseleave", handleMouseLeave);
    };
  }

  private stopListening() {
    this.cleanup();
    this.hoveredElementData = {
      nearestNodeThatCanBeActivated: null,
      nearestRepeatableItem: null,
      nearestWrapperLink: null,
      hoveredGalleryImage: null
    };
  }
}
